import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  AppState,
  StyleSheet,
} from 'react-native';
import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import { Feather } from '@expo/vector-icons';
import { useAppTheme } from '../contexts/ThemeContext';

export const BIOMETRIC_PREF_KEY = 'biometric_enabled';

export default function BiometricLock({ children }) {
  const { colors, typography, spacing } = useAppTheme();
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState('');
  const appState = useRef(AppState.currentState);
  const biometricsAvailable = useRef(false);
  const isAuthenticating = useRef(false);
  const biometricEnabled = useRef(true);

  useEffect(() => {
    (async () => {
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      biometricsAvailable.current = hasHardware && isEnrolled;

      const pref = await SecureStore.getItemAsync(BIOMETRIC_PREF_KEY);
      // Default to enabled if never set
      biometricEnabled.current = pref === null ? true : pref === 'true';
    })();

    const subscription = AppState.addEventListener('change', (nextState) => {
      const wasBackground = appState.current === 'background';
      const nowActive = nextState === 'active';
      if (wasBackground && nowActive && biometricsAvailable.current && biometricEnabled.current && !isAuthenticating.current) {
        setLocked(true);
        setError('');
        promptAuth();
      }
      appState.current = nextState;
    });

    return () => subscription.remove();
  }, []);

  async function promptAuth() {
    if (isAuthenticating.current) return;
    isAuthenticating.current = true;
    try {
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: 'Unlock Slate',
        fallbackLabel: 'Use Passcode',
        disableDeviceFallback: false,
      });
      if (result.success) {
        setLocked(false);
        setError('');
      } else {
        setError('Authentication failed. Tap to try again.');
      }
    } catch {
      setError('Authentication failed. Tap to try again.');
    } finally {
      isAuthenticating.current = false;
    }
  }

  if (!locked) return children;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Text style={[styles.logo, { color: colors.text }]}>Slate</Text>
      <Text style={[styles.sub, { color: colors.textMuted }]}>Contractor Quoting</Text>

      <View style={[styles.iconWrap, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <Feather name="lock" size={32} color={colors.accent} />
      </View>

      <Text style={[styles.message, { color: colors.textSecondary }]}>
        Your account is locked
      </Text>

      {error ? (
        <Text style={[styles.error, { color: colors.danger }]}>{error}</Text>
      ) : null}

      <TouchableOpacity
        style={[styles.btn, { backgroundColor: colors.accent }]}
        onPress={promptAuth}
        activeOpacity={0.8}
      >
        <Feather name="shield" size={16} color="#fff" style={styles.btnIcon} />
        <Text style={styles.btnText}>Unlock Slate</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  logo: {
    fontSize: 42,
    fontWeight: '700',
    letterSpacing: -2,
    marginBottom: 4,
  },
  sub: {
    fontSize: 12,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 48,
  },
  iconWrap: {
    width: 80,
    height: 80,
    borderRadius: 24,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  message: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 8,
    letterSpacing: -0.3,
  },
  error: {
    fontSize: 13,
    marginBottom: 24,
    textAlign: 'center',
  },
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 14,
    marginTop: 24,
    gap: 8,
  },
  btnIcon: {
    marginRight: 4,
  },
  btnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.2,
  },
});
